/**
 * Catalog data model.
 *
 * Extracted from componentCatalog.data.tsx so the per-family entry files can
 * import the types without a cycle (data.tsx aggregates those files).
 *
 * `live`       — rendered from the REAL component so the page can't drift from code.
 * `documented` — real component exists but needs a provider/context/data to render in
 *                isolation; we describe it + its import path instead of rendering it.
 * `gap`        — intended atom that does NOT exist yet (no primitive in the codebase).
 * `needs-work` — exists but off-spec / slated for rework.
 */
import type React from 'react';

export type Tier = 'atom' | 'molecule' | 'organism' | 'template';
export type CatalogStatus = 'live' | 'documented' | 'gap' | 'needs-work';

/**
 * One row of a component's props table.
 *
 * A catalog that shows only a default specimen teaches callers that the
 * component has one appearance, and they go rewrite the markup to get a second
 * one — which is the drift this whole system exists to stop. The parameters ARE
 * the product, so they are documented as data, beside the specimen.
 */
export interface PropDoc {
  name: string;
  type: string;
  /** Omit for a required prop; `required` marks it. */
  default?: string;
  required?: boolean;
  description: string;
}

/** A named specimen. Use several to show what the parameters actually do. */
export interface CatalogExample {
  name: string;
  description?: string;
  render: () => React.ReactNode;
}

export interface CatalogEntry {
  id: string;
  name: string;
  tier: Tier;
  status: CatalogStatus;
  importPath: string;
  description: string;
  /** Present ⇒ live specimen. Absent ⇒ documented/gap (renders a note panel instead). */
  render?: () => React.ReactNode;
  /** Named specimens rendered below the default one, each with its own caption. */
  examples?: CatalogExample[];
  /** Full props table. Sorted as written — most-reached-for first, not alphabetical. */
  props?: PropDoc[];
  /** Copy-paste starting point. */
  usage?: string;
  /** Why it isn't live-rendered (required provider, not built, etc.). */
  note?: string;
}

export const TIER_ORDER: Tier[] = ['atom', 'molecule', 'organism', 'template'];

export const TIER_META: Record<Tier, { label: string; blurb: string }> = {
  atom: { label: 'Atoms', blurb: 'Indivisible primitives — badges, headings, labels, single indicators.' },
  molecule: { label: 'Molecules', blurb: 'Small compositions of atoms — cards, rows, headers, empty states.' },
  organism: { label: 'Organisms', blurb: 'Larger assemblies — row lists, page shells, panels, overlays.' },
  template: { label: 'Templates', blurb: 'Page-level scaffolds that arrange organisms into a screen.' },
};
